/**
 * Currency formatting utilities for Indonesian Rupiah
 */

/**
 * Format number as Indonesian Rupiah currency
 * @param amount - The amount to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export function formatRupiah(
  amount: number,
  options: {
    showSymbol?: boolean;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  } = {}
): string {
  const {
    showSymbol = true,
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
  } = options;

  const formatted = amount.toLocaleString('id-ID', {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return showSymbol ? `Rp ${formatted}` : formatted;
}

/**
 * Format number as compact Indonesian Rupiah (e.g., Rp 1,2 Jt)
 * @param amount - The amount to format
 * @returns Compact formatted currency string
 */
export function formatRupiahCompact(amount: number): string {
  if (amount >= 1_000_000_000) {
    return `Rp ${(amount / 1_000_000_000).toFixed(1)} M`;
  } else if (amount >= 1_000_000) {
    return `Rp ${(amount / 1_000_000).toFixed(1)} Jt`;
  } else if (amount >= 1_000) {
    return `Rp ${(amount / 1_000).toFixed(1)} Rb`;
  } else {
    return `Rp ${amount}`;
  }
}

/**
 * Parse rupiah string back to number
 * @param rupiahString - String in format "Rp 1.000.000" or "1.000.000"
 * @returns Number value
 */
export function parseRupiah(rupiahString: string): number {
  // Remove "Rp", spaces, and dots, then convert to number
  const cleanString = rupiahString
    .replace(/Rp\s*/g, '')
    .replace(/\./g, '')
    .replace(/,/g, '.');
  
  return parseFloat(cleanString) || 0;
}

/**
 * Format currency for display in different contexts
 */
export const CurrencyFormatter = {
  /**
   * Standard format: Rp 1.000.000
   */
  standard: (amount: number) => formatRupiah(amount),
  
  /**
   * Compact format: Rp 1,2 Jt
   */
  compact: (amount: number) => formatRupiahCompact(amount),
  
  /**
   * Number only: 1.000.000 (without Rp symbol)
   */
  numberOnly: (amount: number) => formatRupiah(amount, { showSymbol: false }),
  
  /**
   * With decimals: Rp 1.000.000,50
   */
  withDecimals: (amount: number) => formatRupiah(amount, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  }),
};

/**
 * Parse string number to formatted Indonesian Rupiah
 * @param input - String or number to parse and format
 * @returns Formatted currency string
 */
export function parseAndFormatRupiah(input: string | number): string {
  if (typeof input === 'number') {
    return formatRupiah(input);
  }
  
  if (typeof input === 'string') {
    // Remove all non-digit characters except decimal point
    const cleanedInput = input.replace(/[^\d.,]/g, '');
    
    // Handle Indonesian decimal format (comma as decimal separator)
    const normalizedInput = cleanedInput.replace(',', '.');
    
    const numericValue = parseFloat(normalizedInput);
    
    if (isNaN(numericValue)) {
      return 'Rp 0';
    }
    
    return formatRupiah(numericValue);
  }
  
  return 'Rp 0';
}

/**
 * Parse string number to compact Indonesian Rupiah format
 * @param input - String or number to parse and format compactly
 * @returns Compact formatted currency string
 */
export function parseAndFormatRupiahCompact(input: string | number): string {
  if (typeof input === 'number') {
    return formatRupiahCompact(input);
  }
  
  if (typeof input === 'string') {
    // Remove all non-digit characters except decimal point
    const cleanedInput = input.replace(/[^\d.,]/g, '');
    
    // Handle Indonesian decimal format (comma as decimal separator)
    const normalizedInput = cleanedInput.replace(',', '.');
    
    const numericValue = parseFloat(normalizedInput);
    
    if (isNaN(numericValue)) {
      return 'Rp 0';
    }
    
    return formatRupiahCompact(numericValue);
  }
  
  return 'Rp 0';
}